import { AUTH_API_PATHS } from '@/lib/auth/paths'
import { BRAND_COOKIE, COUNTRY_COOKIE } from '@/lib/auth/marketCookies'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'
import {
  getBaseUrlForBrand,
  getBrandCodeForHost,
  getPrimaryBaseUrl,
  getUkBaseUrl,
  normalizeComparableHost,
  type RegionalBrandCode,
} from '@/lib/seo/marketUrls'

type DomainSwitchStartRouteResponse = {
  success?: boolean
  error?: string | null
  redirectUrl?: string | null
}

export function sanitizeRedirectPath(
  input: string | null | undefined,
  fallback = '/'
): string {
  if (!input) return fallback
  const trimmed = input.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return fallback
  return trimmed
}

export function resolveAllowedTargetBaseUrl(
  rawTargetBaseUrl: string | null | undefined
): string | null {
  if (!rawTargetBaseUrl) return null

  try {
    const requested = new URL(rawTargetBaseUrl)
    const allowed = [getPrimaryBaseUrl(), getUkBaseUrl()]

    return (
      allowed.find(baseUrl => {
        const allowedUrl = new URL(baseUrl)
        return (
          normalizeComparableHost(allowedUrl.host) ===
            normalizeComparableHost(requested.host) &&
          allowedUrl.protocol === requested.protocol
        )
      }) ?? null
    )
  } catch {
    return null
  }
}

export function getBaseUrlForCountryBrand(
  brandCode: RegionalBrandCode
): string {
  return getBaseUrlForBrand(brandCode)
}

export function getBrandCodeForBaseUrl(
  baseUrl: string
): RegionalBrandCode | null {
  try {
    return getBrandCodeForHost(new URL(baseUrl).host)
  } catch {
    return null
  }
}

export function parseRegionalBrandCode(
  value: string | null | undefined
): RegionalBrandCode | null {
  if (value === 'VANISHING_INC_US' || value === 'VANISHING_INC_UK') return value
  return null
}

/** Query flag appended when redirecting into checkout after a US/UK domain switch */
export const CHECKOUT_MARKET_MOVE_SEARCH_PARAM = 'checkoutMarket'

/**
 * Appends {@link CHECKOUT_MARKET_MOVE_SEARCH_PARAM}=1 so the destination checkout page can show a one-time notice.
 */
export function appendCheckoutMarketMoveQuery(redirectPath: string): string {
  const safe = sanitizeRedirectPath(redirectPath, '/')
  try {
    const u = new URL(safe, 'https://placeholder.invalid')
    u.searchParams.set(CHECKOUT_MARKET_MOVE_SEARCH_PARAM, '1')
    return `${u.pathname}${u.search}`
  } catch {
    const joiner = safe.includes('?') ? '&' : '?'
    return `${safe}${joiner}${CHECKOUT_MARKET_MOVE_SEARCH_PARAM}=1`
  }
}

export function consumeCheckoutMarketMoveQuery(redirectPath: string): {
  redirectPath: string
  shouldShowNotice: boolean
} {
  const safe = sanitizeRedirectPath(redirectPath, '/')
  try {
    const u = new URL(safe, 'https://placeholder.invalid')
    const shouldShowNotice =
      u.searchParams.get(CHECKOUT_MARKET_MOVE_SEARCH_PARAM) === '1'
    u.searchParams.delete(CHECKOUT_MARKET_MOVE_SEARCH_PARAM)
    const search = u.searchParams.toString()
    return {
      redirectPath: `${u.pathname}${search ? `?${search}` : ''}`,
      shouldShowNotice,
    }
  } catch {
    return {
      redirectPath: safe,
      shouldShowNotice: safe.includes(`${CHECKOUT_MARKET_MOVE_SEARCH_PARAM}=1`),
    }
  }
}

/**
 * Sends the user to the correct regional host for `brandCode` when it differs from
 * the current page (US .com vs UK .co.uk), using the domain-switch auth flow.
 * When already on that host, runs `onSameHost` instead (e.g. SPA navigation or refetch).
 *
 * @returns true when a full cross-host navigation was started (`location.assign`);
 *   the caller should not `router.push` afterwards. false when the user stays on this host.
 */
export async function navigateToMarketForBrand(options: {
  brandCode: RegionalBrandCode
  redirectPath: string
  selectedCountryIsoCode: string
  shoppingCartToken?: string | null
  onSameHost?: () => void | Promise<void>
}): Promise<boolean> {
  const {
    brandCode,
    redirectPath,
    selectedCountryIsoCode,
    shoppingCartToken,
    onSameHost,
  } = options
  const safeRedirect = sanitizeRedirectPath(redirectPath, '/')
  const targetBaseUrl = getBaseUrlForCountryBrand(brandCode)
  const targetUrl = new URL(safeRedirect, targetBaseUrl)

  if (typeof window === 'undefined') {
    await onSameHost?.()
    return false
  }

  if (
    normalizeComparableHost(window.location.host) ===
    normalizeComparableHost(targetUrl.host)
  ) {
    await onSameHost?.()
    return false
  }

  try {
    const response = await fetchWithCloudflarePreclearance(
      AUTH_API_PATHS.DOMAIN_SWITCH_START,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetBaseUrl,
          redirectTo: safeRedirect,
          selectedCountryIsoCode,
          shoppingCartToken,
        }),
      }
    )

    const payload = (await response
      .json()
      .catch(() => null)) as DomainSwitchStartRouteResponse | null

    if (response.ok && payload?.success && payload.redirectUrl) {
      window.location.assign(payload.redirectUrl)
      return true
    }
  } catch (error) {
    console.error('Failed to start domain switch:', error)
  }

  window.location.assign(targetUrl.toString())
  return true
}

export type CountryBrandRow = {
  isoCode: string
  brandCode?: string | null
}

/**
 * Aligns `vi-brand`, client country state, and host with the market for a shipping
 * country (US .com vs UK .co.uk). Call after the cart’s shipping country is updated
 * server-side (e.g. saved address applied).
 *
 * @returns true if a cross-host `location.assign` was started; caller must not navigate again.
 */
export async function syncClientToRegionalMarketForCountry(options: {
  countryIsoCode: string | null | undefined
  countries: ReadonlyArray<CountryBrandRow>
  setCookie: (name: string, value: string) => void
  setCountry: (iso: string) => void
  redirectPath: string
  shoppingCartToken?: string | null
  /** When true, adds {@link CHECKOUT_MARKET_MOVE_SEARCH_PARAM} so checkout UI can explain the move */
  appendCheckoutMarketMoveNotice?: boolean
  onSameHost?: () => void | Promise<void>
}): Promise<boolean> {
  const iso = options.countryIsoCode?.trim()
  if (!iso) {
    await options.onSameHost?.()
    return false
  }

  const row = options.countries.find(
    c => c.isoCode?.trim().toUpperCase() === iso.toUpperCase()
  )
  const regionalBrand = parseRegionalBrandCode(row?.brandCode)

  if (!regionalBrand) {
    options.setCountry(iso)
    options.setCookie(COUNTRY_COOKIE, iso)
    await options.onSameHost?.()
    return false
  }

  const redirectPath = options.appendCheckoutMarketMoveNotice
    ? appendCheckoutMarketMoveQuery(options.redirectPath)
    : options.redirectPath
  return navigateToMarketForBrand({
    brandCode: regionalBrand,
    redirectPath,
    selectedCountryIsoCode: iso,
    shoppingCartToken: options.shoppingCartToken,
    onSameHost: async () => {
      options.setCountry(iso)
      options.setCookie(COUNTRY_COOKIE, iso)
      if (row?.brandCode) {
        options.setCookie(BRAND_COOKIE, row.brandCode)
      }
      await options.onSameHost?.()
    },
  })
}
